import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { TFunction } from 'i18next'

import { convertAreaLabel } from '~/models/area'

/**
 * backend の上限数量(コマ計) 拒否が ErrorInfo.metadata に載せるトークン
 * (`firstAreaCode` は英小文字、`firstDirection` は buy | sell)。
 * spec AC-BSP-073 / AC-BSP-075 の文言は表示言語のエリア名・売買にする。
 */
const AREA_CODE_BY_TOKEN: Record<string, AreaCode> = {
  hokkaido: AreaCode.HOKKAIDO,
  tohoku: AreaCode.TOHOKU,
  tokyo: AreaCode.TOKYO,
  chubu: AreaCode.CHUBU,
  hokuriku: AreaCode.HOKURIKU,
  kansai: AreaCode.KANSAI,
  chugoku: AreaCode.CHUGOKU,
  shikoku: AreaCode.SHIKOKU,
  kyushu: AreaCode.KYUSHU,
  okinawa: AreaCode.OKINAWA,
}

export type VolumeTotalLimitMessageKeys = {
  /** エリア・コマ・売買・合計・上限を埋め込んだ spec の文。 */
  detail: string
  /** metadata が足りない時の文。穴の空いた文を出さない。 */
  plain: string
}

/**
 * 上限数量(コマ計) の拒否文言。 metadata が揃っている時だけエリア・コマ・売買・
 * 合計・上限を埋め込んだ文にし、欠ける時は plain に戻す。
 */
export function volumeTotalLimitMessage(
  metadata: Record<string, string> | undefined,
  t: TFunction,
  keys: VolumeTotalLimitMessageKeys,
): string {
  const values = volumeTotalLimitInterpolation(metadata, t)
  return values === undefined ? t(keys.plain) : t(keys.detail, values)
}

function volumeTotalLimitInterpolation(
  metadata: Record<string, string> | undefined,
  t: TFunction,
): Record<string, string> | undefined {
  if (metadata === undefined) return undefined
  const areaCode = AREA_CODE_BY_TOKEN[metadata.firstAreaCode ?? '']
  const area = areaCode === undefined ? '' : convertAreaLabel(areaCode)
  const side = tradeSideLabel(metadata.firstDirection, t)
  const koma = metadata.firstTimeCode?.trim() ?? ''
  const total = metadata.firstTotalMw?.trim() ?? ''
  const limit = metadata.firstLimitMw?.trim() ?? ''
  if (
    area === '' ||
    side === undefined ||
    koma === '' ||
    total === '' ||
    limit === ''
  ) {
    return undefined
  }
  return {
    firstAreaCode: area,
    firstTimeCode: koma,
    firstDirection: side,
    firstTotalMw: total,
    firstLimitMw: limit,
  }
}

function tradeSideLabel(
  direction: string | undefined,
  t: TFunction,
): string | undefined {
  switch (direction?.toLowerCase()) {
    case 'buy':
      return t('common:tradeSide.buy')
    case 'sell':
      return t('common:tradeSide.sell')
    default:
      return undefined
  }
}
